import { useCallback, useEffect, useRef } from 'react';

import { getCouponKakaoShareCallbacks } from '@shared/api/giftCoupon/giftCoupon.controller';
import { KakaoShareCallback } from '@shared/api/giftCoupon/giftCoupon.interface';
import { getDeliveryKakaoShareCallbacks } from '@shared/api/giftDelivery/giftDelivery.controller';

export type ShareContentKind = 'coupon' | 'delivery';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 15000;
const INITIAL_DELAY_MS = 1000;

interface UseKakaoShareWebhookCheckOptions {
  onSuccess: (
    kind: ShareContentKind,
    entityId: string,
    callback: KakaoShareCallback,
  ) => void;
  onTimeout: (kind: ShareContentKind, entityId: string) => void;
}

interface ActiveTracker {
  kind: ShareContentKind;
  entityId: string;
  baselineLength: number;
  startedAt: number;
  aborted: boolean;
  pollTimer: ReturnType<typeof setTimeout> | null;
  initialDelayTimer: ReturnType<typeof setTimeout> | null;
}

function fetchCallbacks(kind: ShareContentKind, entityId: string) {
  return kind === 'coupon'
    ? getCouponKakaoShareCallbacks(entityId)
    : getDeliveryKakaoShareCallbacks(entityId);
}

export function useKakaoShareWebhookCheck({
  onSuccess,
  onTimeout,
}: UseKakaoShareWebhookCheckOptions) {
  const trackerRef = useRef<ActiveTracker | null>(null);
  const onSuccessRef = useRef(onSuccess);
  const onTimeoutRef = useRef(onTimeout);
  onSuccessRef.current = onSuccess;
  onTimeoutRef.current = onTimeout;

  const cleanup = useCallback(() => {
    const t = trackerRef.current;
    if (!t) return;
    t.aborted = true;
    if (t.pollTimer) clearTimeout(t.pollTimer);
    if (t.initialDelayTimer) clearTimeout(t.initialDelayTimer);
    trackerRef.current = null;
  }, []);

  const poll = useCallback(() => {
    const t = trackerRef.current;
    if (!t || t.aborted) return;

    if (Date.now() - t.startedAt >= POLL_TIMEOUT_MS) {
      const { kind, entityId } = t;
      cleanup();
      onTimeoutRef.current(kind, entityId);
      return;
    }

    fetchCallbacks(t.kind, t.entityId)
      .then((callbacks) => {
        if (!trackerRef.current || trackerRef.current.aborted) return;
        if (callbacks.length > t.baselineLength) {
          const newest = callbacks[0]!;
          const { kind, entityId } = t;
          cleanup();
          onSuccessRef.current(kind, entityId, newest);
        } else {
          t.pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
        }
      })
      .catch(() => {
        if (!trackerRef.current || trackerRef.current.aborted) return;
        t.pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
      });
  }, [cleanup]);

  const startPollingOnReturn = useCallback(() => {
    const t = trackerRef.current;
    if (!t || t.aborted) return;
    if (t.initialDelayTimer) clearTimeout(t.initialDelayTimer);
    t.initialDelayTimer = setTimeout(poll, INITIAL_DELAY_MS);
  }, [poll]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        startPollingOnReturn();
      }
    };
    const handleFocus = () => {
      startPollingOnReturn();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [startPollingOnReturn]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const start = useCallback(
    async (kind: ShareContentKind, entityId: string) => {
      cleanup();

      let baselineLength = 0;
      try {
        const existing = await fetchCallbacks(kind, entityId);
        baselineLength = existing.length;
      } catch {
        // baseline fetch 실패 시 0으로 시작
      }

      const tracker: ActiveTracker = {
        kind,
        entityId,
        baselineLength,
        startedAt: Date.now(),
        aborted: false,
        pollTimer: null,
        initialDelayTimer: null,
      };
      trackerRef.current = tracker;
    },
    [cleanup],
  );

  const cancel = useCallback(() => {
    cleanup();
  }, [cleanup]);

  return { start, cancel };
}
